import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FilterRow, TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatQty, isPieceUom, qtyInputStep } from '../lib/quantity';
import { WithWrite } from '../lib/rbac';
import {
  ordersApi,
  type OrdersMeta,
  type SalesOrderFormData,
  type SalesOrderListItem,
} from '../services/orders';

type OrderFormRow = {
  productId: number | '';
  productSearch: string;
  qty: string;
  uomItemId: number | '';
  notes: string;
};

const EMPTY_FORM: SalesOrderFormData = {
  customerId: null,
  status: 'DRAFT',
  orderDate: '',
  requestedDeliveryDate: null,
  notes: '',
  rows: [],
};

const EMPTY_ROW: OrderFormRow = {
  productId: '',
  productSearch: '',
  qty: '',
  uomItemId: '',
  notes: '',
};

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function productOptionLabel(product: OrdersMeta['products'][number]): string {
  return product.sku ? `${product.sku} - ${product.name}` : product.name;
}

export function OrdersPage() {
  const [orders, setOrders] = useState<SalesOrderListItem[]>([]);
  const [meta, setMeta] = useState<OrdersMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SalesOrderFormData>(EMPTY_FORM);
  const [formRows, setFormRows] = useState<OrderFormRow[]>([]);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { canRead } = useAuth();

  if (!canRead('orders')) {
    return <Navigate to="/" replace />;
  }

  const loadAll = useCallback(async () => {
    try {
      const [listRes, metaRes] = await Promise.all([ordersApi.list(), ordersApi.meta()]);
      setOrders(listRes.orders);
      setMeta(metaRes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda kundorder');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredOrders = useMemo(
    () =>
      orders.filter((order) => {
        const text = filter.toLowerCase();
        const matchesText =
          !filter ||
          order.orderNumber.toLowerCase().includes(text) ||
          (order.customerName ?? '').toLowerCase().includes(text);
        const matchesStatus = !statusFilter || order.status === statusFilter;
        return matchesText && matchesStatus;
      }),
    [filter, orders, statusFilter]
  );

  const resetForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormRows([]);
    setOpenProductSearchIndex(null);
  };

  const openCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      ...EMPTY_FORM,
      status: 'DRAFT',
      orderDate: today,
      requestedDeliveryDate: today,
    });
    setFormRows([{ ...EMPTY_ROW }]);
    setOpenProductSearchIndex(null);
    setShowForm(true);
  };

  const updateFormRow = (index: number, patch: Partial<OrderFormRow>) => {
    setFormRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    );
  };

  const handleProductSelect = (index: number, productId: number) => {
    if (!meta) return;
    const product = meta.products.find((item) => item.id === productId);
    if (!product) return;
    updateFormRow(index, {
      productId,
      productSearch: productOptionLabel(product),
      uomItemId: product.defaultUomItemId ?? '',
    });
    setOpenProductSearchIndex(null);
  };

  const handleSave = async () => {
    const rows = formRows
      .filter((row) => row.productId !== '' && row.qty.trim() !== '')
      .map((row) => ({
        productId: Number(row.productId),
        qty: Number(row.qty),
        uomItemId: row.uomItemId === '' ? null : Number(row.uomItemId),
        notes: row.notes.trim() || null,
      }));

    const payload: SalesOrderFormData = { ...form, rows };

    setSaving(true);
    try {
      const created = await ordersApi.create(payload);
      toast.success('Order skapad');
      resetForm();
      await loadAll();
      navigate(`/orders/${created.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara order');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !meta) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Kundorder</h1>
        <WithWrite moduleCode="orders">
          <button
            type="button"
            onClick={openCreate}
            className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
          >
            Ny order
          </button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Filtrera på ordernummer eller kund"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="w-72"
        />
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="w-48"
        >
          <option value="">Alla statusar</option>
          {meta.statuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Order</th>
            <th className="px-2 py-1.5 text-left font-medium">Kund</th>
            <th className="px-2 py-1.5 text-left font-medium">Datum</th>
            <th className="px-2 py-1.5 text-left font-medium">Önskad leverans</th>
            <th className="px-2 py-1.5 text-left font-medium">Status</th>
            <th className="px-2 py-1.5 text-left font-medium">Rader</th>
            <th className="px-2 py-1.5 text-left font-medium">Beställt</th>
            <th className="px-2 py-1.5 text-left font-medium">Levererat</th>
          </tr>
          <FilterRow>
            <td colSpan={8} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredOrders.map((order) => (
            <tr
              key={order.id}
              onClick={() => navigate(`/orders/${order.id}`)}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
            >
              <td className="px-2 py-1 font-medium">{order.orderNumber}</td>
              <td className="px-2 py-1">{order.customerName ?? '-'}</td>
              <td className="px-2 py-1">{formatDateOnly(order.orderDate)}</td>
              <td className="px-2 py-1">{formatDateOnly(order.requestedDeliveryDate)}</td>
              <td className="px-2 py-1">{order.status}</td>
              <td className="px-2 py-1">{order.rowCount}</td>
              <td className="px-2 py-1">{formatQty(order.totalQty)}</td>
              <td className="px-2 py-1">{formatQty(order.shippedQty)}</td>
            </tr>
          ))}
          {filteredOrders.length === 0 && (
            <tr>
              <td colSpan={8} className="px-2 py-3 text-sm text-gray-500">
                Inga kundorder eller inga träffar.
              </td>
            </tr>
          )}
        </tbody>
      </TableWrapper>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <h2 className="text-base font-semibold">Ny order</h2>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Ordernummer</label>
                  <input value={meta.nextOrderNumber} readOnly className="w-full bg-gray-50 text-gray-600" />
                  <p className="mt-1 text-xs text-gray-500">Sätts automatiskt när ordern sparas.</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Kund</label>
                  <select
                    value={form.customerId ?? ''}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        customerId: event.target.value ? Number(event.target.value) : null,
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">-</option>
                    {meta.customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Datum *</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    value={form.orderDate}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, orderDate: event.target.value }))
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Önskat leveransdatum</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    value={form.requestedDeliveryDate ?? ''}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        requestedDeliveryDate: event.target.value || null,
                      }))
                    }
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Status</label>
                  <select
                    value={form.status}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, status: event.target.value }))
                    }
                    className="w-full"
                  >
                    {meta.statuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium">Orderrader</h3>
                  <button
                    type="button"
                    onClick={() => setFormRows((current) => [...current, { ...EMPTY_ROW }])}
                    className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
                  >
                    Lägg till rad
                  </button>
                </div>
                <div className="space-y-2">
                  {formRows.map((row, index) => {
                    const selectedUomLabel =
                      meta.uoms.find((uom) => uom.id === (row.uomItemId === '' ? null : row.uomItemId))
                        ?.label ?? null;
                    const matchingProducts = meta.products
                      .filter((product) => {
                        const search = row.productSearch.trim().toLowerCase();
                        if (!search) {
                          return true;
                        }
                        return `${product.sku ?? ''} ${product.name}`.toLowerCase().includes(search);
                      })
                      .slice(0, 12);

                    return (
                      <div key={index} className="grid grid-cols-12 gap-2 rounded border border-gray-200 p-3">
                        <div className="col-span-12 md:col-span-6">
                          <label className="block text-sm text-gray-600">Produkt</label>
                          <div className="relative">
                            <input
                              value={row.productSearch}
                              placeholder="Sök artikelnummer eller namn"
                              onFocus={() => setOpenProductSearchIndex(index)}
                              onChange={(event) =>
                                updateFormRow(index, {
                                  productId: '',
                                  productSearch: event.target.value,
                                })
                              }
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setOpenProductSearchIndex((current) =>
                                    current === index ? null : current
                                  );
                                  setFormRows((current) =>
                                    current.map((entry, rowIndex) => {
                                      if (rowIndex !== index || entry.productId === '') {
                                        return entry;
                                      }
                                      const matchedProduct = meta.products.find(
                                        (product) => product.id === Number(entry.productId)
                                      );
                                      if (!matchedProduct) {
                                        return entry;
                                      }
                                      return {
                                        ...entry,
                                        productSearch: productOptionLabel(matchedProduct),
                                      };
                                    })
                                  );
                                }, 120);
                              }}
                              className="w-full"
                            />
                            {openProductSearchIndex === index && (
                              <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                                {matchingProducts.map((product) => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      handleProductSelect(index, product.id);
                                    }}
                                    className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                  >
                                    {productOptionLabel(product)}
                                  </button>
                                ))}
                                {matchingProducts.length === 0 && (
                                  <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <label className="block text-sm text-gray-600">Mängd</label>
                          <input
                            type="number"
                            min="0"
                            step={qtyInputStep(selectedUomLabel)}
                            value={row.qty}
                            onKeyDown={(event) => {
                              if (isPieceUom(selectedUomLabel) && (event.key === '.' || event.key === ',')) {
                                event.preventDefault();
                              }
                            }}
                            onChange={(event) => updateFormRow(index, { qty: event.target.value })}
                            className="w-full"
                          />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <label className="block text-sm text-gray-600">Enhet</label>
                          <select
                            value={row.uomItemId}
                            onChange={(event) =>
                              updateFormRow(index, {
                                uomItemId: event.target.value ? Number(event.target.value) : '',
                              })
                            }
                            className="w-full"
                          >
                            <option value="">-</option>
                            {meta.uoms.map((uom) => (
                              <option key={uom.id} value={uom.id}>
                                {uom.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-12 md:col-span-2 flex items-end justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              setFormRows((current) => current.filter((_, rowIndex) => rowIndex !== index))
                            }
                            className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50"
                          >
                            Ta bort
                          </button>
                        </div>
                        <div className="col-span-12">
                          <label className="block text-sm text-gray-600">Anteckningar</label>
                          <input
                            value={row.notes}
                            onChange={(event) => updateFormRow(index, { notes: event.target.value })}
                            className="w-full"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea
                  value={form.notes ?? ''}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                  className="min-h-[100px] w-full"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
